import { useMemo } from "react";

import { useMediaQuery } from "../../hooks";
import { useProvideSecondaryNav } from "../../layout/SecondaryNavContext";
import SectionTabs from "../SectionTabs";

/** The width the layout stops rendering the rails at — see `layout/index.js`. */
const RAIL_BREAKPOINT = "(max-width: 992px)";

/**
 * A page's own sections, shown wherever there is room for them.
 *
 * On a wide screen they take over the second rail of the sidebar, which is
 * where the panel already puts the sections of whatever you have open — a
 * project's board, list and reports live there. A row of tabs above the content
 * was a second, competing answer to the same question, and it cost the page a
 * strip of vertical space on every screen.
 *
 * Below the rails' breakpoint there is no second rail at all, so the same items
 * render as the tab strip they used to be. Same contract as the project screen,
 * which has worked this way from the start.
 *
 * `items` and `onChange` must be stable across renders — a module-level array,
 * a `useMemo`, a `useCallback`. A fresh object each render would publish a
 * fresh nav each render, and the layout re-rendering from that would publish
 * another one.
 */
const PageNav = ({ title, items, activeKey, onChange, className }) => {
  const isCompact = useMediaQuery(RAIL_BREAKPOINT);

  const nav = useMemo(
    () => ({ title, items, activeKey, onSelect: onChange }),
    [title, items, activeKey, onChange],
  );

  /* `null` while compact, so the rail is not holding a menu nobody can see —
     and the literal keeps the effect's dependency stable. */
  useProvideSecondaryNav(isCompact ? null : nav);

  if (!isCompact) return null;

  return (
    <SectionTabs
      items={items}
      activeKey={activeKey}
      onChange={onChange}
      className={className}
    />
  );
};

export default PageNav;
